import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Row, Col, Card, Badge, Modal, ListGroup, Button, OverlayTrigger, Tooltip, Form } from 'react-bootstrap';
import {
  FaMicrochip,
  FaSignal,
  FaMapMarkerAlt,
  FaFileContract,
  FaHistory,
  FaInfoCircle,
  FaCalendarAlt,
  FaSearch,
  FaTools,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUserCircle,
  FaMapMarkedAlt,
  FaBoxOpen,
  FaDownload,
  FaSync,
  FaTimes,
  FaCamera
} from 'react-icons/fa';
import Page from '../../components/Page/Page';
import Headers from '../../components/Headers/Headers';
import ModernTable from '../../components/ModernTable/ModernTable';
import Reload from '../../components/reload/reload';
import ApiGServer from '../../services/ApiGServer';
import ApiGSenha from '../../services/ApiGSenha';
import ApiMServer from '../../services/ApiMServer';
import Api from '../../services/Api';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  Chart as ChartJS,
  Tooltip as ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(ChartTooltip, Legend, CategoryScale, LinearScale, BarElement);

// Fuso oficial usado em todos os filtros e exibições do dashboard.
// Garante que qualquer usuário (SP, MT, exterior...) veja o mesmo "Hoje"/"Ontem".
const APP_TIMEZONE = 'America/Sao_Paulo';
const APP_TIMEZONE_LABEL = 'Brasília (UTC−3)';

const appZoneFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: APP_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false
});

// `updatedAt` é gravado pelo servidor em UTC.
// Convertemos para o fuso `APP_TIMEZONE` e devolvemos um `moment` "ingênuo"
// (sem timezone), cujas comparações `isSame(now, 'day')` refletem a meia-noite
// de Brasília — independentemente do fuso do navegador do usuário.
const toAppZoneMoment = (dateInput) => {
  if (!dateInput) return null;
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return null;

  const parts = appZoneFormatter.formatToParts(date).reduce((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = part.value;
    return acc;
  }, {});

  return moment(
    `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`,
    'YYYY-MM-DD HH:mm:ss'
  );
};

const nowInAppZone = () => toAppZoneMoment(new Date());

// Formata bytes em KB / MB com 1 casa decimal — usado no indicador de
// progresso do download do streaming.
const formatBytes = (bytes) => {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const BRAZILIAN_UFS = new Set([
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
]);

const STATE_NAME_TO_UF = {
  acre: 'AC',
  alagoas: 'AL',
  amapa: 'AP',
  amazonas: 'AM',
  bahia: 'BA',
  ceara: 'CE',
  'distrito federal': 'DF',
  'espirito santo': 'ES',
  goias: 'GO',
  maranhao: 'MA',
  'mato grosso': 'MT',
  'mato grosso do sul': 'MS',
  'minas gerais': 'MG',
  para: 'PA',
  paraiba: 'PB',
  parana: 'PR',
  pernambuco: 'PE',
  piaui: 'PI',
  'rio de janeiro': 'RJ',
  'rio grande do norte': 'RN',
  'rio grande do sul': 'RS',
  rondonia: 'RO',
  roraima: 'RR',
  'santa catarina': 'SC',
  'sao paulo': 'SP',
  sergipe: 'SE',
  tocantins: 'TO',
};

const normalizeAddressText = (value) => (
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
);

const isValidBrazilianUf = (uf) => BRAZILIAN_UFS.has(String(uf || '').toUpperCase());

const ufFromStateName = (value) => STATE_NAME_TO_UF[normalizeAddressText(value)] || null;

// Extrai a UF (estado) de endereços em string (gera senha) ou objeto (equipment/filter).
const extractStateFromAddress = (address) => {
  if (!address) return null;

  if (typeof address === 'object') {
    const shortname = address.state?.shortname;
    if (isValidBrazilianUf(shortname)) return shortname.toUpperCase();
    const longname = address.state?.longname;
    return ufFromStateName(longname);
  }

  if (typeof address !== 'string') return null;

  const text = address.trim();
  if (!text) return null;

  const patterns = [
    /-\s*([A-Za-z]{2})\s*,\s*\d{5}-?\d{0,3}/,          // Cidade - UF, CEP
    /,\s*([A-Za-z]{2})\s*,\s*\d{5}-?\d{0,3}/,           // Cidade, UF, CEP
    /-\s*([A-Za-z]{2})\s*,\s*(?:Brazil|Brasil)\s*$/i,   // Cidade - UF, Brazil
    /,\s*([A-Za-z]{2})\s*,\s*(?:Brazil|Brasil)\s*$/i,   // Cidade, UF, Brazil
    /-\s*([A-Za-z]{2})\s*$/,                            // Cidade - UF
    /,\s*([A-Za-z]{2})\s*$/,                            // ..., UF
    /,\s*[^,/]+?\s*\/\s*([A-Za-z]{2})\b/,               // Cidade/UF
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match && isValidBrazilianUf(match[1])) {
      return match[1].toUpperCase();
    }
  }

  const segments = text.split(',').map(part => part.trim()).filter(Boolean);
  for (let i = segments.length - 1; i >= 0; i -= 1) {
    const segment = segments[i];
    if (/^(brazil|brasil)$/i.test(segment)) continue;
    if (/^\d{5}-?\d{0,3}$/.test(segment)) continue;

    const ufSuffixMatch = segment.match(/-\s*([A-Za-z]{2})\s*$/);
    if (ufSuffixMatch && isValidBrazilianUf(ufSuffixMatch[1])) {
      return ufSuffixMatch[1].toUpperCase();
    }

    const ufFromLongNameSuffix = segment.match(/-\s*(.+)$/);
    if (ufFromLongNameSuffix) {
      const ufByName = ufFromStateName(ufFromLongNameSuffix[1]);
      if (ufByName) return ufByName;
    }

    if (/^[A-Za-z]{2}$/.test(segment) && isValidBrazilianUf(segment)) {
      return segment.toUpperCase();
    }

    const ufBySegmentName = ufFromStateName(segment);
    if (ufBySegmentName) return ufBySegmentName;
  }

  const ufMatches = [...text.matchAll(/(?:,\s*|-\s*)([A-Za-z]{2})(?=\s*,|\s*$|\s+\d)/g)];
  for (let i = ufMatches.length - 1; i >= 0; i -= 1) {
    if (isValidBrazilianUf(ufMatches[i][1])) {
      return ufMatches[i][1].toUpperCase();
    }
  }

  return null;
};

const extractCityFromAddress = (address) => {
  if (!address) return null;

  if (typeof address === 'object') {
    return address.city?.longname || address.city?.shortname || null;
  }

  if (typeof address !== 'string') return null;

  const text = address.trim();
  if (!text) return null;

  const cityUfMatch = text.match(/,\s*([^,]+?)\s*-\s*[A-Za-z]{2}\s*,/);
  if (cityUfMatch) return cityUfMatch[1].trim();

  const citySlashUfMatch = text.match(/,\s*([^,/]+?)\s*\/\s*[A-Za-z]{2}\b/);
  if (citySlashUfMatch) return citySlashUfMatch[1].trim();

  const segments = text.split(',').map(part => part.trim()).filter(Boolean);
  for (let i = segments.length - 1; i >= 0; i -= 1) {
    const segment = segments[i];
    if (/^(brazil|brasil)$/i.test(segment)) continue;
    if (/^\d{5}-?\d{0,3}$/.test(segment)) continue;
    if (/^[A-Za-z]{2}$/.test(segment) && isValidBrazilianUf(segment)) continue;
    if (ufFromStateName(segment)) continue;

    const cityFromSuffix = segment.match(/^(.+?)\s*-\s*[A-Za-z]{2}$/);
    if (cityFromSuffix) return cityFromSuffix[1].trim();

    if (i > 0) return segment;
  }

  return null;
};

const buildActivationKpis = (activationList) => {
  const uniqueSerials = new Set();
  const uniqueEmails = new Set();
  const latestBySerial = new Map();
  const latestByEmail = new Map();
  let lastMoment = null;
  let lastActivationItems = [];

  const getItemMoment = (item) => {
    const dateStr = item.createdAt || item.updatedAt;
    return dateStr ? toAppZoneMoment(dateStr) : null;
  };

  const upsertLatest = (map, key, item) => {
    if (!key) return;
    const existing = map.get(key);
    const currentMoment = getItemMoment(item);
    if (!existing) {
      map.set(key, item);
      return;
    }
    const existingMoment = getItemMoment(existing);
    if (currentMoment && (!existingMoment || currentMoment.isAfter(existingMoment))) {
      map.set(key, item);
    }
  };

  (activationList || []).forEach(item => {
    const serial = item.serialnumber || item.sn;
    if (serial) uniqueSerials.add(serial);
    if (item.email) uniqueEmails.add(item.email.toLowerCase());

    upsertLatest(latestBySerial, serial, item);
    upsertLatest(latestByEmail, item.email?.toLowerCase(), item);

    const moment = getItemMoment(item);
    if (moment) {
      if (!lastMoment || moment.isAfter(lastMoment)) {
        lastMoment = moment;
        lastActivationItems = [item];
      } else if (moment.isSame(lastMoment)) {
        lastActivationItems.push(item);
      }
    }
  });

  return {
    total: (activationList || []).length,
    uniqueSerials: uniqueSerials.size,
    uniqueEmails: uniqueEmails.size,
    lastActivation: lastMoment,
    allItems: activationList || [],
    uniqueSerialItems: Array.from(latestBySerial.values()),
    uniqueUserItems: Array.from(latestByEmail.values()),
    lastActivationItems,
  };
};

const DashboardEquipments = () => {
  const [loading, setLoading] = useState(true);
  const [equipments, setEquipments] = useState([]);
  const [equipmentsStatus, setEquipmentsStatus] = useState([]);
  const [todayActivations, setTodayActivations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEquip, setSelectedEquip] = useState(null);
  const [selectedActivation, setSelectedActivation] = useState(null);
  const [detailModalMode, setDetailModalMode] = useState('equipment');
  const [showModal, setShowModal] = useState(false);
  const [responsibleDetails, setResponsibleDetails] = useState({});
  const [faceModal, setFaceModal] = useState({ show: false, image: null, serial: '' });
  const [openedFromChart, setOpenedFromChart] = useState(false);
  // Chart List Modal State
  const [chartModal, setChartModal] = useState({ show: false, title: '', items: [], itemType: 'equipment' });
  const [modalSearchTerm, setModalSearchTerm] = useState('');

  // Activation Chart Filters
  const todayInAppZone = nowInAppZone().format('YYYY-MM-DD');
  const [activationPeriod, setActivationPeriod] = useState('today');
  const [customDates, setCustomDates] = useState({
    start: todayInAppZone,
    end: todayInAppZone
  });
  const [appliedCustomDates, setAppliedCustomDates] = useState({
    start: todayInAppZone,
    end: todayInAppZone
  });

  const handleApplyCustomFilter = () => {
    setAppliedCustomDates({ ...customDates });
    setActivationPeriod('custom');
  };

  // Pagination State
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Modal de confirmação do download
  const [downloadConfirm, setDownloadConfirm] = useState({ show: false, variant: null });

  // Resultado da busca de ativações no servidor gera senha (ApiGSenha), conforme o período.
  // `null` = ainda não retornou (renderização inicial).
  const [periodEquipments, setPeriodEquipments] = useState(null);
  const [periodLoading, setPeriodLoading] = useState(false);
  // Bytes recebidos do stream (atualizado por onDownloadProgress do axios) —
  // mostra progresso na UI durante respostas grandes do streaming do backend.
  const [periodLoadingBytes, setPeriodLoadingBytes] = useState(0);
  // Cancela requisições anteriores quando o usuário troca de filtro rápido,
  // evitando que uma resposta atrasada sobrescreva o estado da request atual.
  const periodAbortRef = useRef(null);
  const skipInitialPeriodFetchRef = useRef(true);

  const fetchUserDetails = useCallback(async (equipmentList, activationList) => {
    const emails = new Set();
    (equipmentList || []).forEach(e => {
      if (e.lastEmailActivation) emails.add(e.lastEmailActivation.toLowerCase());
    });
    (activationList || []).forEach(a => {
      if (a.email) emails.add(a.email.toLowerCase());
    });

    if (emails.size === 0) {
      setResponsibleDetails({});
      return;
    }

    try {
      const { data: userData } = await ApiMServer.post('/user/v1/details-by-email', {
        emails: [...emails],
        fields: ['name', 'email', 'cpfcnpj', 'fone']
      });

      const detailsMap = {};
      if (Array.isArray(userData)) {
        userData.forEach(user => {
          if (user?.email) {
            detailsMap[user.email.toLowerCase()] = user;
          }
        });
      }
      setResponsibleDetails(prev => ({ ...prev, ...detailsMap }));
    } catch (err) {
      console.error('Erro ao buscar detalhes dos responsáveis:', err);
    }
  }, []);

  const fetchTodayActivations = useCallback(async (storedSerials, { setAsPeriod = false } = {}) => {
    const { data } = await ApiGSenha.post('/activations/period', {
      equipments: storedSerials,
      period: 'today',
      timezone: APP_TIMEZONE
    });
    const list = Array.isArray(data) ? data : [];
    setTodayActivations(list);
    if (setAsPeriod) setPeriodEquipments(list);
    return list;
  }, []);

  const fetchActivationsForPeriod = useCallback(async ({ period, startDate, endDate }) => {
    const storedSerials = JSON.parse(localStorage.getItem('equipment')) || [];
    if (storedSerials.length === 0) {
      setPeriodEquipments([]);
      return;
    }

    if (periodAbortRef.current) periodAbortRef.current.abort();
    const controller = new AbortController();
    periodAbortRef.current = controller;

    setPeriodLoading(true);
    setPeriodLoadingBytes(0);
    try {
      const { data } = await ApiGSenha.post(
        '/activations/period',
        {
          equipments: storedSerials,
          period,
          startDate,
          endDate,
          timezone: APP_TIMEZONE
        },
        {
          signal: controller.signal,
          // O backend faz streaming via cursor; respostas grandes podem
          // demorar minutos. Damos folga no timeout.
          timeout: 5 * 60 * 1000,
          responseType: 'json',
          // Recebe eventos conforme os bytes chegam do stream — usado pra
          // mostrar feedback de progresso na UI.
          onDownloadProgress: (progressEvent) => {
            if (periodAbortRef.current !== controller) return;
            setPeriodLoadingBytes(progressEvent.loaded || 0);
          }
        }
      );
      const list = Array.isArray(data) ? data : [];
      setPeriodEquipments(list);
      await fetchUserDetails(equipments, list);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      if (err?.code === 'ECONNABORTED') {
        console.error('Timeout ao buscar ativações por período. Tente um período menor.');
      } else {
        console.error('Erro ao buscar ativações por período:', err);
      }
      setPeriodEquipments([]);
    } finally {
      if (periodAbortRef.current === controller) {
        periodAbortRef.current = null;
        setPeriodLoading(false);
      }
    }
  }, [equipments, fetchUserDetails]);

  const handleClearFilters = useCallback(() => {
    const today = nowInAppZone().format('YYYY-MM-DD');
    const wasAlreadyToday =
      activationPeriod === 'today' &&
      appliedCustomDates.start === today &&
      appliedCustomDates.end === today;

    setCustomDates({ start: today, end: today });
    setAppliedCustomDates({ start: today, end: today });
    setActivationPeriod('today');

    const storedSerials = JSON.parse(localStorage.getItem('equipment')) || [];
    if (storedSerials.length === 0) {
      setPeriodEquipments([]);
      return;
    }

    // Se já estava em "hoje", o useEffect não dispara — forçamos nova busca.
    if (wasAlreadyToday) {
      fetchActivationsForPeriod({ period: 'today' });
    }
  }, [activationPeriod, appliedCustomDates, fetchActivationsForPeriod]);

  // Dispara busca do período ao mudar filtro (a carga inicial de "hoje" vem do fetchData).
  useEffect(() => {
    if (skipInitialPeriodFetchRef.current) {
      skipInitialPeriodFetchRef.current = false;
      return;
    }

    if (activationPeriod === 'custom') {
      const start = moment(appliedCustomDates.start, 'YYYY-MM-DD').startOf('day');
      const end = moment(appliedCustomDates.end, 'YYYY-MM-DD').endOf('day');
      fetchActivationsForPeriod({
        period: 'custom',
        startDate: start.isValid() ? start.format() : undefined,
        endDate: end.isValid() ? end.format() : undefined
      });
    } else {
      fetchActivationsForPeriod({ period: activationPeriod });
    }
  }, [activationPeriod, appliedCustomDates, fetchActivationsForPeriod]);

  const fetchData = useCallback(async ({ setPeriodFromToday = false } = {}) => {
    setLoading(true);
    try {
      const storedSerials = JSON.parse(localStorage.getItem('equipment')) || [];

      if (storedSerials.length === 0) {
        setEquipments([]);
        setEquipmentsStatus([]);
        setTodayActivations([]);
        setPeriodEquipments([]);
        setResponsibleDetails({});
        return;
      }

      const { data: details } = await ApiGServer.post('/equipment/filter', { filter: storedSerials });
      const equipmentList = details || [];
      setEquipments(equipmentList);

      const [todayList, statusesResult] = await Promise.all([
        fetchTodayActivations(storedSerials, { setAsPeriod: setPeriodFromToday }),
        Api.post('/api/v1/lastbenchone', { equipments: storedSerials })
          .catch(err => {
            console.error('Erro ao buscar status dos equipamentos:', err);
            return { data: [] };
          }),
      ]);
      setEquipmentsStatus(statusesResult.data || []);

      await fetchUserDetails(equipmentList, todayList);
    } catch (error) {
      console.error('Erro ao buscar dados do dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchTodayActivations, fetchUserDetails]);

  const handleRefresh = useCallback(async () => {
    await fetchData({ setPeriodFromToday: false });

    if (activationPeriod === 'custom') {
      const start = moment(appliedCustomDates.start, 'YYYY-MM-DD').startOf('day');
      const end = moment(appliedCustomDates.end, 'YYYY-MM-DD').endOf('day');
      await fetchActivationsForPeriod({
        period: 'custom',
        startDate: start.isValid() ? start.format() : undefined,
        endDate: end.isValid() ? end.format() : undefined
      });
    } else {
      await fetchActivationsForPeriod({ period: activationPeriod });
    }
  }, [activationPeriod, appliedCustomDates, fetchActivationsForPeriod, fetchData]);

  useEffect(() => {
    fetchData({ setPeriodFromToday: true });
    return () => {
      if (periodAbortRef.current) periodAbortRef.current.abort();
    };
  }, [fetchData]);

  const stats = useMemo(() => {
    const total = equipments.length;
    const blocked = equipments.filter(e => parseInt(e.daystoactivate, 16) === 0).length;
    const rentalMode = equipments.filter(e => e.mode === 'RENTAL').length;

    return {
      total,
      blocked,
      rentalMode
    };
  }, [equipments]);

  // Fonte oficial = retorno do servidor gera senha. Antes do primeiro fetch
  // completar, mantemos vazio (o card mostra o estado de "Buscando…").
  const equipmentsInPeriod = useMemo(() => {
    return Array.isArray(periodEquipments) ? periodEquipments : [];
  }, [periodEquipments]);

  const openChartModal = useCallback(({ title, items, itemType = 'equipment' }) => {
    setModalSearchTerm('');
    setChartModal({ show: true, title, items, itemType });
  }, []);

  const matchesChartModalSearch = useCallback((item, search, itemType) => {
    if (!search) return true;
    const term = search.toUpperCase();
    if (itemType === 'activation') {
      return [item.serialnumber, item.sn, item.email, item.name, item.address, item.equipid]
        .filter(Boolean)
        .some(value => String(value).toUpperCase().includes(term));
    }
    return (item.serialnumber || '').toUpperCase().includes(term) ||
      (item.address?.city?.longname || '').toUpperCase().includes(term);
  }, []);

  const chartBarHoverCursor = (event, elements) => {
    const target = event.native?.target;
    if (target) target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
  };

  const closeDetailModal = useCallback(() => {
    setShowModal(false);
    if (openedFromChart) {
      setChartModal(prev => ({ ...prev, show: true }));
    }
    setOpenedFromChart(false);
  }, [openedFromChart]);

  const openEquipmentDetail = useCallback((item, fromChart = false) => {
    setOpenedFromChart(fromChart);
    if (fromChart) {
      setChartModal(prev => ({ ...prev, show: false }));
    }
    setDetailModalMode('equipment');
    setSelectedEquip(item);
    setSelectedActivation(null);
    setShowModal(true);
  }, []);

  const openActivationDetail = useCallback((item) => {
    setOpenedFromChart(true);
    setChartModal(prev => ({ ...prev, show: false }));
    setDetailModalMode('activation');
    setSelectedActivation(item);
    setSelectedEquip(null);
    setShowModal(true);
  }, []);

  const linkedEquipmentForActivation = useMemo(() => {
    if (!selectedActivation) return null;
    const serial = selectedActivation.serialnumber || selectedActivation.sn;
    return equipments.find(e => e.serialnumber === serial) || null;
  }, [selectedActivation, equipments]);

  // Equipamentos ativados HOJE — baseado em activations/period (period=today).
  const todayEquipments = useMemo(() => {
    const todaySerials = new Set(
      todayActivations
        .map(item => item.serialnumber || item.sn)
        .filter(Boolean)
    );
    return equipments.filter(e => todaySerials.has(e.serialnumber));
  }, [equipments, todayActivations]);

  // ─────────────────────────────────────────────────────────────────────────
  // Memos do card "Ativações por Período" — cada item é um EVENTO retornado
  // pelo servidor gera senha (mesmo serial pode aparecer várias vezes).
  // ─────────────────────────────────────────────────────────────────────────
  const activationKpis = useMemo(
    () => buildActivationKpis(equipmentsInPeriod),
    [equipmentsInPeriod]
  );

  const topUsersStats = useMemo(() => {
    const counts = {};
    const itemsByUser = {};
    const labelByEmail = {};

    equipmentsInPeriod.forEach(item => {
      const email = (item.email || 'desconhecido').toLowerCase();
      counts[email] = (counts[email] || 0) + 1;
      if (!itemsByUser[email]) itemsByUser[email] = [];
      itemsByUser[email].push(item);
      if (!labelByEmail[email]) labelByEmail[email] = item.name || item.email || 'Desconhecido';
    });

    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 10);
    return {
      labels: sorted.map(([email]) => labelByEmail[email]),
      emails: sorted.map(([email]) => email),
      data: sorted.map(([, n]) => n),
      items: sorted.map(([email]) => itemsByUser[email])
    };
  }, [equipmentsInPeriod]);

  // Distribuição Geográfica (UF) — igual ao card de "Ativações de Hoje":
  // mostra "Com Bandeja" e "Sem Bandeja" e abre o modal com os eventos do estado.
  //
  // Regra de UF:
  // - 1) tenta primeiro via `equipments` (address estruturado, reduz N/A)
  // - 2) fallback: parse do `address` string vindo do activations/period (evento mais recente)
  const geoStats = useMemo(() => {
    const counts = {};
    const countsWithTray = {};
    const countsWithoutTray = {};
    const itemsByState = {};
    const itemsByStateWithTray = {};
    const itemsByStateWithoutTray = {};

    const equipmentBySerial = new Map((equipments || []).map(e => [e.serialnumber, e]));

    equipmentsInPeriod.forEach(item => {
      const serial = item.serialnumber || item.sn;
      const equip = serial ? equipmentBySerial.get(serial) : null;
      const state =
        extractStateFromAddress(equip?.address) ||
        extractStateFromAddress(item.address) ||
        'N/A';
      const hasTray = equip?.tray === true;

      counts[state] = (counts[state] || 0) + 1;

      if (!itemsByState[state]) {
        itemsByState[state] = [];
        itemsByStateWithTray[state] = [];
        itemsByStateWithoutTray[state] = [];
      }

      itemsByState[state].push(item);

      if (hasTray) {
        countsWithTray[state] = (countsWithTray[state] || 0) + 1;
        itemsByStateWithTray[state].push(item);
      } else {
        countsWithoutTray[state] = (countsWithoutTray[state] || 0) + 1;
        itemsByStateWithoutTray[state].push(item);
      }
    });

    const sortedStates = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);

    return {
      labels: sortedStates,
      data: sortedStates.map(s => counts[s]),
      withTrayData: sortedStates.map(s => countsWithTray[s] || 0),
      withoutTrayData: sortedStates.map(s => countsWithoutTray[s] || 0),
      items: sortedStates.map(s => itemsByState[s]),
      itemsWithTray: sortedStates.map(s => itemsByStateWithTray[s] || []),
      itemsWithoutTray: sortedStates.map(s => itemsByStateWithoutTray[s] || []),
      totalCount: equipmentsInPeriod.length
    };
  }, [equipments, equipmentsInPeriod]);

  const todayActivationStats = useMemo(() => {
    const withTrayItems = todayEquipments.filter(e => e.tray === true);
    const withoutTrayItems = todayEquipments.filter(e => e.tray !== true);

    return {
      withTray: withTrayItems.length,
      withoutTray: withoutTrayItems.length,
      withTrayItems,
      withoutTrayItems,
    };
  }, [todayEquipments]);

  // Distribuição Geográfica — bar horizontal por UF (stacked), igual ao "Ativações de Hoje"
  const geoBarData = useMemo(() => ({
    labels: geoStats.labels,
    datasets: [
      {
        label: 'Com Bandeja',
        data: geoStats.withTrayData,
        backgroundColor: '#8fad15',
        borderRadius: 4,
        borderWidth: 0,
      },
      {
        label: 'Sem Bandeja',
        data: geoStats.withoutTrayData,
        backgroundColor: '#adb5bd',
        borderRadius: 4,
        borderWidth: 0,
      }
    ],
  }), [geoStats]);

  const geoBarOptions = useMemo(() => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'top', align: 'end', labels: { boxWidth: 10, font: { size: 10 } } },
      tooltip: { backgroundColor: '#1c4220', padding: 10, cornerRadius: 8 }
    },
    scales: {
      x: { stacked: true, beginAtZero: true, grid: { display: false } },
      y: { stacked: true, grid: { display: false }, ticks: { font: { weight: 'bold' } } }
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const state = geoStats.labels[index];
        openChartModal({
          title: `Ativações em ${state}`,
          items: geoStats.items[index],
          itemType: 'activation'
        });
      }
    },
    onHover: chartBarHoverCursor
  }), [geoStats, openChartModal]);

  // Top usuários — bar horizontal
  const topUsersData = useMemo(() => ({
    labels: topUsersStats.labels,
    datasets: [
      {
        label: 'Ativações',
        data: topUsersStats.data,
        backgroundColor: '#1c8a3a',
        borderRadius: 4,
      }
    ]
  }), [topUsersStats]);

  const topUsersOptions = useMemo(() => ({
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1c4220',
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          afterLabel: (ctx) => {
            const email = topUsersStats.emails[ctx.dataIndex];
            return email && email !== ctx.label.toLowerCase() ? email : '';
          }
        }
      }
    },
    scales: {
      x: { beginAtZero: true, ticks: { stepSize: 1 }, grid: { display: false } },
      y: { grid: { display: false }, ticks: { font: { size: 11 } } }
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const label = topUsersStats.labels[index];
        openChartModal({
          title: `Ativações de ${label}`,
          items: topUsersStats.items[index],
          itemType: 'activation'
        });
      }
    },
    onHover: chartBarHoverCursor
  }), [topUsersStats, openChartModal]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(0);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(0);
  };

  // Tabela = todos os equipamentos filtrados apenas pela busca textual
  const displayedEquipments = useMemo(() => {
    const search = searchTerm.toUpperCase();
    return equipments.filter(equip => {
      if (!search) return true;
      const email = (equip.lastEmailActivation || '').toUpperCase();
      const serial = (equip.serialnumber || '').toUpperCase();
      const city = (equip.address?.city?.longname || '').toUpperCase();
      const plan = (equip.plan || '').toUpperCase();
      return (
        serial.includes(search) ||
        city.includes(search) ||
        plan.includes(search) ||
        email.includes(search)
      );
    });
  }, [equipments, searchTerm]);

  const hasActiveFilters = Boolean(searchTerm);

  const mapActivationForCsvExport = useCallback((activation) => {
    const serial = activation.serialnumber || activation.sn || '';
    const equip = equipments.find(e => e.serialnumber === serial) || {};
    const email = activation.email || equip.lastEmailActivation || '';
    const dateStr = activation.createdAt || activation.updatedAt;
    const parsedCity = extractCityFromAddress(activation.address);
    const parsedState = extractStateFromAddress(activation.address);

    return {
      ...equip,
      serialnumber: serial || equip.serialnumber || '',
      equipid: activation.equipid || equip.equipid || '',
      lastEmailActivation: email,
      versaosw: activation.versaosw || equip.versaosw || '',
      versaofwpri: activation.versaofwpri || equip.versaofwpri || '',
      versaofwosc: activation.versaofwosc || equip.versaofwosc || '',
      lat: activation.lat || equip.lat || '',
      lng: activation.lng || equip.lng || '',
      address: equip.address?.city?.longname
        ? equip.address
        : {
            city: { longname: parsedCity || '' },
            state: { shortname: parsedState || '' },
            formatedAddr: typeof activation.address === 'string'
              ? activation.address
              : (equip.address?.formatedAddr || ''),
          },
      _csvActivationDate: dateStr
        ? toAppZoneMoment(dateStr)?.format('DD/MM/YYYY HH:mm') || ''
        : '',
      _csvResponsibleName: activation.name || '',
    };
  }, [equipments]);

  const generateCSV = (data, filenameSuffix) => {
    if (data.length === 0) return;

    const headers = [
      'Serial Number',
      'ID do Copo',
      'Plano',
      'Cidade',
      'Estado',
      'Responsável (Nome)',
      'Responsável (CPF/CNPJ)',
      'Responsável (Telefone)',
      'E-mail de Ativação',
      'Dias para Ativar',
      'Última Ativação',
      'Data de Calibração',
      'Software Master (SW)',
      'Firmware Principal',
      'Firmware Oscilador',
      'Bandeja Detectada',
      'Autenticação Facial',
      'Rastreamento Completo',
      'Latitude',
      'Longitude',
    ];

    const rows = data.map(e => {
      const user = responsibleDetails[e.lastEmailActivation] || {};
      const activationDate = e._csvActivationDate || (
        getStatus(e.serialnumber)?.unlockData?.updatedAt
          ? toAppZoneMoment(getStatus(e.serialnumber).unlockData.updatedAt).format('DD/MM/YYYY HH:mm')
          : ''
      );
      return [
        `"${e.serialnumber || ''}"`,
        `"${e.cupid || ''}"`,
        `"${e.plan || ''}"`,
        `"${e.address?.city?.longname || ''}"`,
        `"${e.address?.state?.shortname || ''}"`,
        `"${e._csvResponsibleName || user.name || ''}"`,
        `"${user.cpfcnpj || ''}"`,
        `"${user.fone || ''}"`,
        `"${e.lastEmailActivation || ''}"`,
        `"${parseInt(e.daystoactivate, 16) || 0}"`,
        `"${activationDate}"`,
        `"${e.dcal || ''}"`,
        `"${e.versaosw || ''}"`,
        `"${e.versaofwpri || ''}"`,
        `"${e.versaofwosc || ''}"`,
        `"${e.tray ? 'Ativo' : 'Inativo'}"`,
        `"${e.scan_face ? 'Ativo' : 'Inativo'}"`,
        `"${e.full_tracking ? 'Ativo' : 'Inativo'}"`,
        `"${e.lat || ''}"`,
        `"${e.lng || ''}"`,
      ].join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Equipamentos_${filenameSuffix}_${moment().format('DD-MM-YYYY_HH-mm')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Os exports usam o conjunto atualmente visível na tabela
  const exportVariants = {
    all: { suffix: 'Export', label: 'Todos visíveis', data: () => displayedEquipments },
    tray: { suffix: 'Com_Bandeja', label: 'Apenas com bandeja', data: () => displayedEquipments.filter(e => e.tray === true) },
    noTray: { suffix: 'Sem_Bandeja', label: 'Sem bandeja', data: () => displayedEquipments.filter(e => e.tray !== true) },
    period: {
      suffix: `Periodo_${activationPeriod}`,
      label: 'Ativações no período',
      data: () => equipmentsInPeriod.map(mapActivationForCsvExport),
      isPeriod: true
    }
  };

  const requestDownload = (variant) => setDownloadConfirm({ show: true, variant });
  const cancelDownload = () => setDownloadConfirm({ show: false, variant: null });
  const confirmDownload = () => {
    const variant = exportVariants[downloadConfirm.variant];
    if (variant) {
      generateCSV(variant.data(), variant.suffix);
    }
    cancelDownload();
  };

  const downloadChartModalItemsCsv = useCallback(() => {
    if (!chartModal?.show) return;
    if (chartModal.itemType !== 'activation') return;

    const filtered = (chartModal.items || []).filter(item =>
      matchesChartModalSearch(item, modalSearchTerm, chartModal.itemType)
    );

    generateCSV(filtered.map(mapActivationForCsvExport), `Modal_${chartModal.title || 'Ativacoes'}`);
  }, [chartModal, matchesChartModalSearch, modalSearchTerm, mapActivationForCsvExport]);


  const getStatus = useCallback((serial) => {
    return equipmentsStatus.find(s => s.serialnumber === serial) || { status: 'offline', unlockData: null };
  }, [equipmentsStatus]);

  const columns = [
    "Série",
    "Responsável",
    "CPF/CNPJ",
    "Telefone",
    "Localização",
    "Dias para ativação",
    "Última Ativação"
  ];

  const formatDuration = (date, patternText) => {
    if (!date) return 'N/A';
    const now = new Date();
    const diff = Math.abs(now - new Date(date));
    const duration = moment.duration(diff);

    if (duration.asMinutes() < 60) {
      return `${patternText} ${Math.floor(duration.asMinutes())} ${duration.asMinutes() > 1 ? "minutos" : "minuto"}`;
    } else if (duration.asHours() < 24) {
      return `${patternText} ${Math.floor(duration.asHours())} ${duration.asHours() > 1 ? "horas" : "hora"}`;
    } else {
      return `${patternText} ${Math.floor(duration.asDays())} ${duration.asDays() > 1 ? "dias" : "dia"}`;
    }
  };

  const tableData = useMemo(() => {
    const start = currentPage * rowsPerPage;
    const end = start + rowsPerPage;
    const currentItems = displayedEquipments.slice(start, end);

    return currentItems.map(equip => {
      const status = getStatus(equip.serialnumber);
      const emailKey = equip.lastEmailActivation?.toLowerCase();
      const user = responsibleDetails[emailKey] || {};
      const daysLeft = parseInt(equip.daystoactivate, 16);

      return [
        <div className="d-flex align-items-center justify-content-center">
          <strong style={{ color: '#1c4220' }}>{equip.serialnumber}</strong>
        </div>,
        <div style={{ fontSize: '0.8rem', maxWidth: '180px', textAlign: 'center' }} className="text-truncate mx-auto" title={user.name || equip.lastEmailActivation}>
          {user.name || equip.lastEmailActivation || <span className="text-muted small">N/A</span>}
        </div>,
        <div style={{ fontSize: '0.8rem' }}>
          {user.cpfcnpj || <span className="text-muted small">N/A</span>}
        </div>,
        <div style={{ fontSize: '0.8rem' }}>
          {user.fone || <span className="text-muted small">N/A</span>}
        </div>,
        <div style={{ fontSize: '0.85rem' }}>
          {equip.address?.city?.shortname || 'N/A'}, {equip.address?.state?.shortname || ''}
        </div>,
        <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: daysLeft === 0 ? '#dc3545' : '#444' }}>
          {daysLeft || 0} dias
        </div>,
        <div style={{ fontSize: '0.85rem' }}>
          {formatDuration(status?.unlockData?.updatedAt, 'há')}
        </div>
      ];
    });
  }, [displayedEquipments, currentPage, rowsPerPage, equipmentsStatus, getStatus, responsibleDetails]);

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(0);
  };

  const pagination = {
    currentPage,
    totalPages: Math.ceil(displayedEquipments.length / rowsPerPage),
    rowsPerPage,
    total: displayedEquipments.length,
    currentStart: currentPage * rowsPerPage,
    currentEnd: (currentPage + 1) * rowsPerPage,
    onChangePage: handlePageChange,
    onChangeRowsPerPage: handleRowsPerPageChange,
    pageNumbers: Array.from({ length: Math.ceil(displayedEquipments.length / rowsPerPage) }, (_, i) => i)
  };

  return (
    <Page title={"Dashboard de Equipamentos"}>
      <Headers page={"Dashboard de Equipamentos"} />

      {loading && <Reload />}
      {periodLoading && <Reload />}

      <div className="container-fluid px-0 py-3">
        {/* Stats Section */}
        <Row className="mb-4 g-3">
          <Col xs={6} md={3}>
            <StatCard
              title="Total de Equipamentos"
              value={stats.total}
              icon={<FaMicrochip />}
              color="linear-gradient(135deg, #1c4220 0%, #8fad15 100%)"
              onClick={() => openChartModal({
                title: 'Total de Equipamentos',
                items: equipments
              })}
            />
          </Col>
          <Col xs={6} md={3}>
            <StatCard
              title="Ativos com Bandeja (Hoje)"
              value={todayActivationStats.withTray}
              subValue="Ativados hoje com bandeja"
              icon={<FaSignal />}
              color="linear-gradient(135deg, #28a745 0%, #20c997 100%)"
              onClick={() => openChartModal({
                title: 'Ativos com Bandeja (Hoje)',
                items: todayActivationStats.withTrayItems
              })}
            />
          </Col>
          <Col xs={6} md={3}>
            <StatCard
              title="Ativos sem Bandeja (Hoje)"
              value={todayActivationStats.withoutTray}
              subValue="Ativados hoje sem bandeja"
              icon={<FaFileContract />}
              color="linear-gradient(135deg, #FF8C00 0%, #FFA500 100%)"
              onClick={() => openChartModal({
                title: 'Ativos sem Bandeja (Hoje)',
                items: todayActivationStats.withoutTrayItems
              })}
            />
          </Col>
          <Col xs={6} md={3}>
            <StatCard
              title="Equipamentos Bloqueados"
              value={stats.blocked}
              icon={<FaExclamationTriangle />}
              color={stats.blocked > 0 ? "linear-gradient(135deg, #dc3545 0%, #f8615a 100%)" : "linear-gradient(135deg, #6c757d 0%, #adb5bd 100%)"}
              onClick={() => openChartModal({
                title: 'Equipamentos Bloqueados',
                items: equipments.filter(e => parseInt(e.daystoactivate, 16) === 0)
              })}
            />
          </Col>
        </Row>

        {/* Card "Ativações por Período" — consulta ativações no servidor gera senha */}
        <Card className="border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
          <Card.Header className="bg-white border-0 pt-4 px-4 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 border-bottom">
            <div className="d-flex flex-column">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <h5 className="fw-bold mb-0">Ativações por Período</h5>
                <Button
                  variant="outline-success"
                  size="sm"
                  className="d-inline-flex align-items-center gap-2 rounded-pill"
                  onClick={handleRefresh}
                  disabled={loading || periodLoading}
                >
                  <FaSync />
                  Atualizar
                </Button>
              </div>
            </div>
            <div className="d-flex flex-column flex-lg-row align-items-center gap-3 w-100 w-lg-auto">
              <div className="d-flex flex-wrap justify-content-center gap-1 p-1 rounded-pill border shadow-sm w-100 w-lg-auto" style={{ width: 'fit-content', backgroundColor: '#f0f0f0' }}>
                {[{ key: 'today', label: 'Hoje' }, { key: 'yesterday', label: 'Ontem' }, { key: 'week', label: 'Semana' }, { key: 'month', label: 'Mês' }].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setActivationPeriod(key)}
                    style={{
                      fontSize: '0.72rem',
                      fontWeight: '600',
                      padding: '4px 14px',
                      borderRadius: '999px',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: activationPeriod === key ? '#1c8a3a' : '#ffffff',
                      color: activationPeriod === key ? '#ffffff' : '#444444',
                      boxShadow: activationPeriod === key ? '0 2px 6px rgba(28,138,58,0.35)' : 'none',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="d-flex flex-wrap justify-content-center align-items-center p-1 bg-light rounded-pill border shadow-sm w-100 w-lg-auto ms-lg-2">
                <div className="d-flex align-items-center px-3 border-end">
                  <FaCalendarAlt className="text-muted me-2" style={{ fontSize: '0.8rem' }} />
                  <Form.Control
                    type="date"
                    size="sm"
                    className="border-0 bg-transparent shadow-none p-0 text-muted"
                    style={{ fontSize: '0.75rem', width: '105px', outline: 'none' }}
                    value={customDates.start}
                    onChange={(e) => setCustomDates({ ...customDates, start: e.target.value })}
                  />
                  <span className="text-muted mx-2 fw-medium" style={{ fontSize: '0.75rem' }}>até</span>
                  <Form.Control
                    type="date"
                    size="sm"
                    className="border-0 bg-transparent shadow-none p-0 text-muted"
                    style={{ fontSize: '0.75rem', width: '105px', outline: 'none' }}
                    value={customDates.end}
                    onChange={(e) => setCustomDates({ ...customDates, end: e.target.value })}
                  />
                </div>
                <div className="d-flex gap-1 px-1 ms-1">
                  <Button
                    variant="success"
                    size="sm"
                    className="rounded-pill px-3 py-1 shadow-none border-0 d-flex align-items-center"
                    style={{ fontSize: '0.7rem', fontWeight: '600' }}
                    onClick={handleApplyCustomFilter}
                  >
                    <FaSearch className="me-1 mb-0" style={{ fontSize: '0.65rem' }} /> Filtrar
                  </Button>
                  <Button
                    variant="light"
                    size="sm"
                    className="rounded-pill px-3 py-1 shadow-none border d-flex align-items-center text-muted"
                    style={{ fontSize: '0.7rem', fontWeight: '600', backgroundColor: '#fff' }}
                    onClick={handleClearFilters}
                  >
                    <FaTimes className="me-1 mb-0" style={{ fontSize: '0.65rem' }} /> Limpar
                  </Button>
                </div>
              </div>
            </div>
          </Card.Header>

          <Card.Body className="p-4 bg-light bg-opacity-10">
            <ActivationKpisRow
              kpis={activationKpis}
              scopeLabel="no período"
              openChartModal={openChartModal}
            />

            {/* Gráficos */}
            <Row className="g-3 mb-3">
              <Col xs={12} lg={6}>
                <Card className="border-0 shadow-sm h-100 rounded-4 overflow-hidden">
                  <Card.Header className="bg-white border-0 px-4 d-flex justify-content-between align-items-center" style={{ minHeight: '60px' }}>
                    <h6 className="fw-bold mb-0" style={{ fontSize: '1rem' }}>Top 10 responsáveis por ativações</h6>
                    <Badge bg="success" className="rounded-pill fw-normal shadow-sm" style={{ fontSize: '0.65rem' }}>
                      {topUsersStats.labels.length} usuário(s)
                    </Badge>
                  </Card.Header>
                  <Card.Body className="p-3">
                    <div style={{ height: '340px', width: '100%' }}>
                      {topUsersStats.labels.length > 0 ? (
                        <Bar data={topUsersData} options={topUsersOptions} />
                      ) : (
                        <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                          Nenhuma ativação no período
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} lg={6}>
                <Card className="border-0 shadow-sm h-100 rounded-4 overflow-hidden">
                  <Card.Header className="bg-white border-0 px-4 d-flex justify-content-between align-items-center" style={{ minHeight: '60px' }}>
                    <h6 className="fw-bold mb-0" style={{ fontSize: '1rem' }}>Distribuição Geográfica</h6>
                    <Badge bg="success" className="rounded-pill fw-normal shadow-sm" style={{ fontSize: '0.65rem' }}>
                      {geoStats.labels.length} estado(s)
                    </Badge>
                  </Card.Header>
                  <Card.Body className="p-3">
                    <div style={{ height: '340px', width: '100%' }}>
                      {geoStats.labels.length > 0 ? (
                        <Bar data={geoBarData} options={geoBarOptions} />
                      ) : (
                        <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                          Nenhum dado
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Botão de export CSV */}
            <div className="d-flex justify-content-end">
              <Button
                variant="dark"
                className="d-flex align-items-center justify-content-center px-4 rounded-pill shadow-sm"
                onClick={() => requestDownload('period')}
                disabled={equipmentsInPeriod.length === 0 || periodLoading}
              >
                <FaDownload className="me-2" /> Exportar CSV
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Main Table Tools Header */}
        <Card className="border-0 shadow-sm rounded-top-4 overflow-hidden mb-4">
          <Card.Body className="p-4 bg-white d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <h5 className="fw-bold mb-1">Lista de Equipamentos</h5>
              <div className="text-muted small d-flex align-items-center flex-wrap gap-2">
                <span>
                  <FaTools className="me-2 text-success" />
                  Exibindo <strong>{displayedEquipments.length}</strong> de <strong>{equipments.length}</strong> equipamentos
                </span>
                {searchTerm && (
                  <Badge bg="info" pill className="fw-normal" style={{ fontSize: '0.7rem' }}>
                    Busca: "{searchTerm}"
                  </Badge>
                )}
              </div>
            </div>

            <div className="d-flex flex-column flex-md-row gap-3 flex-grow-1 justify-content-end" style={{ maxWidth: '850px' }}>
              <div className="position-relative flex-grow-1">
                <FaSearch className="position-absolute translate-middle-y top-50 ms-3 text-muted" />
                <input
                  type="text"
                  className="form-control ps-5 py-2 border-0 bg-light rounded-pill"
                  placeholder="Pesquisar por série, cidade, plano..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{ boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)' }}
                />
                {searchTerm && (
                  <Button
                    variant="link"
                    className="position-absolute translate-middle-y top-50 end-0 text-muted p-0 pe-3 text-decoration-none"
                    onClick={clearSearch}
                  >
                    <FaTimes />
                  </Button>
                )}
              </div>
              <Button
                variant="dark"
                className="d-flex align-items-center justify-content-center px-4 rounded-pill shadow-sm"
                onClick={() => requestDownload('all')}
                disabled={displayedEquipments.length === 0}
              >
                <FaDownload className="me-2" /> Exportar CSV
              </Button>
              <Button
                variant="success"
                className="d-flex align-items-center justify-content-center px-4 rounded-pill shadow-sm"
                onClick={() => requestDownload('tray')}
                disabled={displayedEquipments.filter(e => e.tray === true).length === 0}
              >
                <FaDownload className="me-2" /> Com Bandeja
              </Button>
              <Button
                variant="secondary"
                className="d-flex align-items-center justify-content-center px-4 rounded-pill shadow-sm"
                onClick={() => requestDownload('noTray')}
                disabled={displayedEquipments.filter(e => e.tray !== true).length === 0}
              >
                <FaDownload className="me-2" /> Sem Bandeja
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Main Table */}
        <ModernTable
          columns={columns}
          data={tableData}
          pagination={pagination}
          onRowClick={(index) => {
            const currentItems = displayedEquipments.slice(pagination.currentStart, pagination.currentEnd);
            const equip = currentItems[index];
            if (equip) {
              openEquipmentDetail(equip, false);
            }
          }}
        />
      </div>

      {/* Download Confirmation Modal */}
      <Modal show={downloadConfirm.show} onHide={cancelDownload} centered contentClassName="rounded-4 border-0 shadow-lg">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold d-flex align-items-center" style={{ color: '#1c4220' }}>
            <FaDownload className="me-2" />
            Confirmar exportação
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 pt-2 pb-3">
          {(() => {
            if (!downloadConfirm.variant) return null;
            const variant = exportVariants[downloadConfirm.variant];
            const data = variant.data();
            const isPeriodExport = variant.isPeriod;
            return (
              <>
                <p className="mb-3">
                  Você está prestes a exportar <strong>{data.length}</strong>{' '}
                  {isPeriodExport ? 'ativação(ões)' : 'equipamento(s)'} para CSV.
                </p>

                <div className="mb-3">
                  <small className="text-muted d-block mb-1 text-uppercase fw-bold" style={{ fontSize: '0.7rem' }}>
                    Filtros aplicados
                  </small>
                  <ul className="list-unstyled mb-0 small">
                    <li>
                      <strong>Conjunto:</strong> {variant.label}
                    </li>
                    {isPeriodExport ? (
                      <li>
                        <strong>Período:</strong>{' '}
                        {activationPeriod === 'custom'
                          ? `${moment(appliedCustomDates.start).format('DD/MM/YYYY')} até ${moment(appliedCustomDates.end).format('DD/MM/YYYY')}`
                          : ({
                              today: 'Hoje',
                              yesterday: 'Ontem',
                              week: 'Última semana',
                              month: 'Último mês'
                            }[activationPeriod] || activationPeriod)}
                      </li>
                    ) : (
                      <li>
                        <strong>Busca textual:</strong> {searchTerm ? `"${searchTerm}"` : <span className="text-muted">nenhuma</span>}
                      </li>
                    )}
                    <li>
                      <strong>Total no sistema:</strong>{' '}
                      {isPeriodExport
                        ? `${equipmentsInPeriod.length} ativação(ões) no período`
                        : `${equipments.length} equipamento(s)`}
                    </li>
                  </ul>
                </div>

                {isPeriodExport ? (
                  <div className="alert alert-info d-flex align-items-start gap-2 small mb-0 py-2">
                    <FaInfoCircle className="mt-1" />
                    <div>
                      Exportação no mesmo formato da lista de equipamentos. Cada linha representa uma ativação do período, enriquecida com dados do equipamento quando disponível.
                    </div>
                  </div>
                ) : hasActiveFilters ? (
                  <div className="alert alert-warning d-flex align-items-start gap-2 small mb-0 py-2">
                    <FaExclamationTriangle className="mt-1" />
                    <div>
                      <strong>Atenção:</strong> a exportação respeita a busca textual. Equipamentos fora dela não entram no arquivo.
                    </div>
                  </div>
                ) : (
                  <div className="alert alert-success d-flex align-items-start gap-2 small mb-0 py-2">
                    <FaCheckCircle className="mt-1" />
                    <div>Nenhum filtro aplicado — todos os equipamentos serão exportados.</div>
                  </div>
                )}
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="light" onClick={cancelDownload} className="rounded-pill px-3">
            Cancelar
          </Button>
          <Button variant="success" onClick={confirmDownload} className="rounded-pill px-4 d-flex align-items-center">
            <FaDownload className="me-2" /> Baixar CSV
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Details Modal */}
      <Modal
        show={showModal}
        onHide={closeDetailModal}
        size="xl"
        centered
        contentClassName="rounded-4 border-0 shadow-lg"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-success d-flex align-items-center">
            <FaMicrochip className="me-2" />
            {detailModalMode === 'activation'
              ? `Detalhes da Ativação: ${selectedActivation?.serialnumber || selectedActivation?.sn || '—'}`
              : `Detalhes do Equipamento: ${selectedEquip?.serialnumber}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 bg-light">
          {detailModalMode === 'activation' && selectedActivation && (
            <Card className="border-0 shadow-sm rounded-4">
              <Card.Body className="p-4">
                <Row className="g-4">
                  <Col md={4}>
                    <h6 className="fw-bold mb-3 text-muted text-uppercase small">Detalhes da Ativação</h6>
                    <ListGroup variant="flush" className="mb-4">
                      <DetailItem
                        icon={<FaHistory />}
                        label="Data da Ativação"
                        value={(() => {
                          const dateStr = selectedActivation.createdAt || selectedActivation.updatedAt;
                          return dateStr ? toAppZoneMoment(dateStr)?.format('DD/MM/YYYY HH:mm:ss') : 'N/A';
                        })()}
                      />
                      <DetailItem icon={<FaUserCircle />} label="Responsável" value={selectedActivation.name || 'N/A'} />
                      <DetailItem icon={<FaUserCircle />} label="E-mail" value={selectedActivation.email || 'N/A'} />
                      <DetailItem icon={<FaTools />} label="Equip ID (Módulo)" value={<span className="text-break">{selectedActivation.equipid || 'N/A'}</span>} />
                    </ListGroup>

                    <h6 className="fw-bold mb-3 text-muted text-uppercase small">Versões (OTA)</h6>
                    <ListGroup variant="flush" className="mb-4">
                      <DetailItem icon={<FaMicrochip />} label="Software Master (SW)" value={<Badge bg="dark">{selectedActivation.versaosw || 'N/A'}</Badge>} />
                      <DetailItem icon={<FaMicrochip />} label="Firmware Principal" value={selectedActivation.versaofwpri || 'N/A'} />
                      <DetailItem icon={<FaMicrochip />} label="Firmware Oscilador" value={selectedActivation.versaofwosc || 'N/A'} />
                    </ListGroup>

                    {linkedEquipmentForActivation && (
                      <>
                        <h6 className="fw-bold mb-3 text-muted text-uppercase small">Recursos do Equipamento</h6>
                        <ListGroup variant="flush">
                          <DetailItem icon={<FaFileContract />} label="Plano" value={linkedEquipmentForActivation.plan || 'N/A'} />
                          <DetailItem icon={<FaBoxOpen />} label="Bandeja Detectada" value={linkedEquipmentForActivation.tray ? <Badge bg="success">Ativo</Badge> : <Badge bg="secondary">Inativo</Badge>} />
                          <DetailItem icon={<FaUserCircle />} label="Autenticação Facial" value={linkedEquipmentForActivation.scan_face ? <Badge bg="success">Ativo</Badge> : <Badge bg="secondary">Inativo</Badge>} />
                          <DetailItem icon={<FaMapMarkedAlt />} label="Rastreamento em Tempo Real" value={linkedEquipmentForActivation.full_tracking ? <Badge bg="success">Ativo</Badge> : <Badge bg="secondary">Inativo</Badge>} />
                        </ListGroup>
                      </>
                    )}
                  </Col>

                  <Col md={8}>
                    <h6 className="fw-bold mb-3 text-muted text-uppercase small">Localização Identificada</h6>
                    <div className="bg-light p-2 rounded-3 text-center h-100 d-flex flex-column overflow-hidden border">
                      {selectedActivation.lat && selectedActivation.lng ? (
                        <div className="rounded-3 overflow-hidden mb-3 border shadow-sm position-relative" style={{ height: '350px', backgroundColor: '#e5e3df' }}>
                          <iframe
                            width="100%"
                            height="100%"
                            frameBorder="0"
                            style={{ border: 0 }}
                            src={`https://maps.google.com/maps?q=${selectedActivation.lat},${selectedActivation.lng}&z=15&t=k&output=embed`}
                            allowFullScreen
                            title="Localização da Ativação"
                          ></iframe>
                        </div>
                      ) : (
                        <div className="bg-white rounded-3 mb-3 d-flex flex-column align-items-center justify-content-center border" style={{ height: '350px' }}>
                          <FaMapMarkerAlt size={48} className="text-danger mb-2 opacity-25" />
                          <small className="text-muted">Coordenadas GPS não vinculadas</small>
                        </div>
                      )}
                      <div className="px-3 pb-3">
                        <h5 className="fw-bold mb-2">
                          {extractCityFromAddress(selectedActivation.address) || 'Localização'}
                          {extractStateFromAddress(selectedActivation.address) ? `, ${extractStateFromAddress(selectedActivation.address)}` : ''}
                        </h5>
                        <p className="text-muted mb-0">
                          {selectedActivation.address || 'Endereço não informado'}
                        </p>
                        <div className="mt-3 pt-3 border-top d-flex justify-content-center gap-4 text-muted">
                          <span><strong>Lat:</strong> {selectedActivation.lat || '--'}</span>
                          <span><strong>Lng:</strong> {selectedActivation.lng || '--'}</span>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {detailModalMode === 'equipment' && selectedEquip && (
            <Card className="border-0 shadow-sm rounded-4">
              <Card.Body className="p-4">
                <Row className="g-4">
                  <Col md={4}>
                    <h6 className="fw-bold mb-3 text-muted text-uppercase small">Detalhes do Registro</h6>
                    <ListGroup variant="flush" className="mb-4">
                      <DetailItem
                        icon={<FaHistory />}
                        label="Última Ativação"
                        value={formatDuration(getStatus(selectedEquip.serialnumber)?.unlockData?.updatedAt, 'há')}
                      />
                      <DetailItem icon={<FaUserCircle />} label="Ativado por" value={selectedEquip.lastEmailActivation || 'N/A'} />
                      <DetailItem icon={<FaFileContract />} label="Plano" value={selectedEquip.plan} />
                      <DetailItem icon={<FaTools />} label="Equip ID (Módulo)" value={<span className="text-break">{selectedEquip.equipid}</span>} />
                    </ListGroup>

                    <h6 className="fw-bold mb-3 text-muted text-uppercase small">Versões (OTA)</h6>
                    <ListGroup variant="flush" className="mb-4">
                      <DetailItem icon={<FaMicrochip />} label="Software Master (SW)" value={<Badge bg="dark">{selectedEquip.versaosw}</Badge>} />
                      <DetailItem icon={<FaMicrochip />} label="Firmware Principal" value={selectedEquip.versaofwpri} />
                      <DetailItem icon={<FaMicrochip />} label="Firmware Oscilador" value={selectedEquip.versaofwosc} />
                    </ListGroup>

                    <h6 className="fw-bold mb-3 text-muted text-uppercase small">Recursos Especiais</h6>
                    <ListGroup variant="flush">
                      <DetailItem icon={<FaBoxOpen />} label="Bandeja Detectada" value={selectedEquip.tray ? <Badge bg="success">Ativo</Badge> : <Badge bg="secondary">Inativo</Badge>} />
                      <DetailItem icon={<FaUserCircle />} label="Autenticação Facial" value={selectedEquip.scan_face ? <Badge bg="success">Ativo</Badge> : <Badge bg="secondary">Inativo</Badge>} />
                      <DetailItem icon={<FaMapMarkedAlt />} label="Rastreamento em Tempo Real" value={selectedEquip.full_tracking ? <Badge bg="success">Ativo</Badge> : <Badge bg="secondary">Inativo</Badge>} />
                    </ListGroup>
                  </Col>

                  <Col md={8}>
                    <h6 className="fw-bold mb-3 text-muted text-uppercase small">Localização Identificada</h6>
                    <div className="bg-light p-2 rounded-3 text-center h-100 d-flex flex-column overflow-hidden border">
                      {selectedEquip.lat && selectedEquip.lng ? (
                        <div className="rounded-3 overflow-hidden mb-3 border shadow-sm position-relative" style={{ height: '350px', backgroundColor: '#e5e3df' }}>
                          <iframe
                            width="100%"
                            height="100%"
                            frameBorder="0"
                            style={{ border: 0 }}
                            src={`https://maps.google.com/maps?q=${selectedEquip.lat},${selectedEquip.lng}&z=15&t=k&output=embed`}
                            allowFullScreen
                            title="Localização do Equipamento"
                          ></iframe>
                        </div>
                      ) : (
                        <div className="bg-white rounded-3 mb-3 d-flex flex-column align-items-center justify-content-center border" style={{ height: '350px' }}>
                          <FaMapMarkerAlt size={48} className="text-danger mb-2 opacity-25" />
                          <small className="text-muted">Coordenadas GPS não vinculadas</small>
                        </div>
                      )}
                      <div className="px-3 pb-3">
                        <h5 className="fw-bold mb-2">{selectedEquip.address?.city?.longname || 'Desconhecida'}, {selectedEquip.address?.state?.shortname || ''}</h5>
                        <p className="text-muted mb-0">
                          {selectedEquip.address?.formatedAddr}
                        </p>
                        <div className="mt-3 pt-3 border-top d-flex justify-content-center gap-4 text-muted">
                          <span><strong>Lat:</strong> {selectedEquip.lat || '--'}</span>
                          <span><strong>Lng:</strong> {selectedEquip.lng || '--'}</span>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="light" onClick={closeDetailModal} className="rounded-3 px-4">
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Chart List Modal */}
      <Modal
        show={chartModal.show}
        onHide={() => {
          setChartModal({ show: false, title: '', items: [], itemType: 'equipment' });
          setModalSearchTerm('');
        }}
        size="xl"
        centered
        contentClassName="rounded-4 border-0 shadow-lg"
      >
        <Modal.Header closeButton className="border-0 bg-light rounded-top-4">
          <Modal.Title className="fw-bold text-dark h5 mb-0">
            {chartModal.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="p-3 bg-white border-bottom">
            <div className="position-relative">
              <FaSearch className="position-absolute translate-middle-y top-50 ms-3 text-muted" />
              <input
                type="text"
                className="form-control ps-5 py-2 border-0 bg-light rounded-3"
                placeholder={chartModal.itemType === 'activation' ? 'Filtrar por serial, usuário, e-mail ou endereço...' : 'Filtrar nesta lista...'}
                value={modalSearchTerm}
                onChange={(e) => setModalSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <ListGroup variant="flush">
              {chartModal.items
                .filter(item => matchesChartModalSearch(item, modalSearchTerm, chartModal.itemType))
                .map((item, idx) => {
                  if (chartModal.itemType === 'activation') {
                    const dateStr = item.createdAt || item.updatedAt;
                    const dateFmt = dateStr ? toAppZoneMoment(dateStr)?.format('DD/MM/YYYY HH:mm:ss') : '—';
                    return (
                      <ListGroup.Item
                        key={`${item._id || item.serialnumber || item.sn}-${idx}`}
                        className="py-3 px-4"
                        action
                        onClick={() => openActivationDetail(item)}
                      >
                        <Row className="align-items-center g-2">
                          <Col xs={12} md={3}>
                            <div className="fw-bold text-success h6 mb-0">{item.serialnumber || item.sn || '—'}</div>
                            <small className="text-muted d-block text-truncate" title={item.equipid}>
                              {item.equipid || 'Equip ID não informado'}
                            </small>
                          </Col>
                          <Col xs={12} md={3}>
                            <div className="fw-medium text-dark">{item.name || 'Desconhecido'}</div>
                            <small className="text-muted d-block text-truncate" title={item.email}>
                              <FaUserCircle className="me-1" /> {item.email || '—'}
                            </small>
                          </Col>
                          <Col xs={12} md={4}>
                            <small className="text-muted d-block text-truncate" title={item.address}>
                              <FaMapMarkerAlt className="me-1" /> {item.address || 'Endereço não informado'}
                            </small>
                          </Col>
                          <Col xs={12} md={2} className="text-md-end">
                            <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                              <FaHistory className="me-1" /> {dateFmt}
                            </small>
                            {item.versaosw && (
                              <small className="text-muted d-block mt-1" style={{ fontSize: '0.65rem' }}>
                                SW: {item.versaosw}
                              </small>
                            )}
                          </Col>
                        </Row>
                      </ListGroup.Item>
                    );
                  }

                  const status = getStatus(item.serialnumber);
                  return (
                    <ListGroup.Item
                      key={idx}
                      className="py-3 px-4"
                      action
                      onClick={() => openEquipmentDetail(item, true)}
                    >
                      <Row className="align-items-center g-2">
                        <Col xs={3}>
                          <div className="fw-bold text-success h6 mb-0">{item.serialnumber}</div>
                          <small className="text-muted d-block text-truncate" title={item.address?.city?.longname}>
                            {item.address?.city?.longname || 'N/A'}
                          </small>
                        </Col>
                        <Col xs={3}>
                          <div className="d-flex flex-column gap-1">
                            <div className="d-flex gap-1 flex-wrap">
                              <Badge bg="success" className="text-light fw-normal" style={{ backgroundColor: '#e3f2fd', fontSize: '0.65rem' }}>
                                Plan: {item.plan || 'N/A'}
                              </Badge>
                              <Badge bg="light" className="text-muted fw-normal border" style={{ fontSize: '0.65rem' }}>
                                Mode: {item.mode || 'N/A'}
                              </Badge>
                            </div>
                            <div className="small text-muted mt-1 text-truncate" style={{ fontSize: '0.65rem' }} title={item.lastEmailActivation}>
                              <FaUserCircle className="me-1" /> {item.lastEmailActivation || 'Desconhecido'}
                            </div>
                            <div className="d-flex gap-2 mt-1">
                              {item.tray && <OverlayTrigger placement="top" overlay={<Tooltip>Bandeja Ativa</Tooltip>}><span className="text-success small"><FaBoxOpen /></span></OverlayTrigger>}
                              {item.scan_face && <OverlayTrigger placement="top" overlay={<Tooltip>Scan Face Ativo</Tooltip>}><span className="text-primary small"><FaUserCircle /></span></OverlayTrigger>}
                              {item.full_tracking && <OverlayTrigger placement="top" overlay={<Tooltip>Full Tracking Ativo</Tooltip>}><span className="text-info small"><FaMapMarkedAlt /></span></OverlayTrigger>}
                            </div>
                          </div>
                        </Col>
                        <Col xs={3}>
                          <div className="small text-muted" style={{ fontSize: '0.7rem' }}>
                            <div><strong>Versão de Software:</strong> {item.versaosw}</div>
                          </div>
                        </Col>
                        <Col xs={3} className="text-end">
                          <div className="small text-muted" style={{ fontSize: '0.65rem' }}>
                            {status?.unlockData?.updatedAt ? formatDuration(status.unlockData.updatedAt, 'Última ativação há') : 'Sem registro'}
                          </div>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  );
                })}
              {chartModal.items.length === 0 && (
                <div className="p-5 text-center text-muted">
                  {chartModal.itemType === 'activation' ? 'Nenhuma ativação encontrada.' : 'Nenhum equipamento encontrado.'}
                </div>
              )}
            </ListGroup>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 bg-light rounded-bottom-4">
          <div className="me-auto text-muted small ps-3">
            Mostrando: <strong>{chartModal.items.filter(item => matchesChartModalSearch(item, modalSearchTerm, chartModal.itemType)).length}</strong> de {chartModal.items.length}
          </div>
          {chartModal.itemType === 'activation' && (
            <Button
              variant="success"
              className="rounded-3 px-4 d-flex align-items-center"
              onClick={downloadChartModalItemsCsv}
              disabled={chartModal.items.filter(item => matchesChartModalSearch(item, modalSearchTerm, chartModal.itemType)).length === 0}
            >
              <FaDownload className="me-2" />
              Baixar CSV
            </Button>
          )}
          <Button variant="dark" onClick={() => {
            setChartModal({ show: false, title: '', items: [], itemType: 'equipment' });
            setModalSearchTerm('');
          }} className="rounded-3 px-4">
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Face Preview Modal */}
      <Modal
        show={faceModal.show}
        onHide={() => setFaceModal({ ...faceModal, show: false })}
        centered
        size="md"
        contentClassName="rounded-4 border-0 shadow-lg overflow-hidden"
      >
        <Modal.Header closeButton className="border-0 bg-dark text-white px-4 py-3">
          <Modal.Title className="h6 fw-bold mb-0">
            <FaCamera className="me-2" />
            Face Autenticada: {faceModal.serial}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 bg-dark d-flex align-items-center justify-content-center" style={{ minHeight: '300px' }}>
          {faceModal.image && (
            <img
              src={`data:image/jpeg;base64,${faceModal.image}`}
              alt="Face Autenticada"
              className="img-fluid"
              style={{ maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 bg-dark p-3">
          <Button variant="outline-light" onClick={() => setFaceModal({ ...faceModal, show: false })} className="rounded-pill px-4 btn-sm">
            Fechar Visualização
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .stat-card-hover {
          transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
        }
        .stat-card-hover:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 20px rgba(0,0,0,0.1) !important;
        }
        .hover-scale {
          transition: transform 0.2s ease;
        }
        .hover-scale:hover {
          transform: translateY(-3px) scale(1.02);
        }
        .custom-pills .nav-link {
          color: #495057;
          font-weight: 500;
          padding: 0.75rem 1.25rem;
          border-radius: 50rem;
        }
        .custom-pills .nav-link:hover {
          background-color: #f8f9fa;
        }
        .custom-pills .nav-link.active {
          background-color: #1c4220;
          color: white;
          box-shadow: 0 4px 6px rgba(28, 66, 32, 0.2);
        }
        @keyframes spinner-rotate-kf {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .spinner-rotate {
          display: inline-block;
          animation: spinner-rotate-kf 0.9s linear infinite;
        }
      `}</style>
    </Page>
  );
};

const ActivationKpisRow = ({ kpis, scopeLabel, openChartModal }) => (
  <Row className="g-3 mb-3">
    <Col xs={6} md={3}>
      <ActivationKpiCard
        label="Total de ativações"
        value={kpis.total}
        valueColor="#1c4220"
        disabled={kpis.total === 0}
        onClick={() => openChartModal({
          title: `Total de ativações ${scopeLabel}`,
          items: kpis.allItems,
          itemType: 'activation',
        })}
      />
    </Col>
    <Col xs={6} md={3}>
      <ActivationKpiCard
        label="Equipamentos únicos"
        value={kpis.uniqueSerials}
        valueColor="#198754"
        disabled={kpis.uniqueSerials === 0}
        onClick={() => openChartModal({
          title: `Equipamentos únicos ${scopeLabel}`,
          items: kpis.uniqueSerialItems,
          itemType: 'activation',
        })}
      />
    </Col>
    <Col xs={6} md={3}>
      <ActivationKpiCard
        label="Usuários únicos"
        value={kpis.uniqueEmails}
        valueColor="#1c8a3a"
        disabled={kpis.uniqueEmails === 0}
        onClick={() => openChartModal({
          title: `Usuários únicos ${scopeLabel}`,
          items: kpis.uniqueUserItems,
          itemType: 'activation',
        })}
      />
    </Col>
    <Col xs={6} md={3}>
      <ActivationKpiCard
        label="Última ativação"
        disabled={!kpis.lastActivation}
        onClick={() => openChartModal({
          title: kpis.lastActivation
            ? `Última ativação ${scopeLabel} (${kpis.lastActivation.format('DD/MM/YYYY HH:mm')})`
            : 'Última ativação',
          items: kpis.lastActivationItems,
          itemType: 'activation',
        })}
      >
        <div className="fw-bold mt-1" style={{ color: '#1c4220', fontSize: '1rem', lineHeight: 1.2 }}>
          {kpis.lastActivation ? kpis.lastActivation.format('DD/MM/YYYY') : '—'}
        </div>
        {kpis.lastActivation && (
          <small className="text-muted" style={{ fontSize: '0.7rem' }}>
            às {kpis.lastActivation.format('HH:mm')}
          </small>
        )}
      </ActivationKpiCard>
    </Col>
  </Row>
);

const ActivationKpiCard = ({ label, value, valueColor, disabled, onClick, children }) => (
  <Card
    className={`border-0 shadow-sm rounded-4 h-100 ${disabled ? '' : 'stat-card-hover'}`}
    style={{ cursor: disabled ? 'default' : 'pointer' }}
    onClick={disabled ? undefined : onClick}
  >
    <Card.Body className="p-3 d-flex flex-column">
      <small className="text-muted" style={{ fontSize: '0.7rem' }}>{label}</small>
      {children || (
        <h3 className="fw-bold mb-0 mt-1" style={{ color: valueColor || '#1c4220' }}>{value}</h3>
      )}
    </Card.Body>
  </Card>
);

const StatCard = ({ title, value, icon, color, subValue, onClick }) => (
  <Card
    className="border-0 shadow-sm rounded-4 stat-card-hover"
    style={{
      background: color,
      color: 'white',
      height: '100%',
      cursor: onClick ? 'pointer' : 'default'
    }}
    onClick={onClick}
  >
    <Card.Body className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="opacity-75">{title}</div>
        <div style={{ fontSize: '1.5rem', opacity: 0.8 }}>{icon}</div>
      </div>
      <h2 className="fw-bold mb-1">{value}</h2>
      {subValue && <div className="small opacity-75">{subValue}</div>}
    </Card.Body>
  </Card>
);

const DetailItem = ({ icon, label, value }) => (
  <ListGroup.Item className="d-flex align-items-center justify-content-between py-2 px-3 border-0 border-bottom">
    <div className="d-flex align-items-center border-0">
      <span className="me-2 text-success opacity-75">{icon}</span>
      <span className="text-muted small">{label}:</span>
    </div>
    <span className="fw-medium text-dark text-end ms-2" style={{ fontSize: '0.85rem' }}>
      {value || 'N/A'}
    </span>
  </ListGroup.Item>
);

export default DashboardEquipments;
